import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SupabaseService } from '../services/supabaseService';
import { useAuth } from '../context/AuthContext';
import { War } from '../types';
import { Scroll, PlusCircle, Swords, History, ChevronRight, Crown, Shield, Flag } from 'lucide-react';
import { QuickPlayModal } from '../components/QuickPlayModal';

const Campaigns: React.FC = () => {
    const navigate = useNavigate();
    const { signOut, user } = useAuth();
    const [wars, setWars] = useState<War[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showTimeout, setShowTimeout] = useState(false);
    const [isQuickPlayModalOpen, setIsQuickPlayModalOpen] = useState(false); // Added state

    useEffect(() => {
        // Timeout watchdog
        const timer = setTimeout(() => {
            if (loading) setShowTimeout(true);
        }, 5000); // 5s timeout

        SupabaseService.getAllWars()
            .then(w => {
                setWars(w);
                setLoading(false);
            })
            .catch(err => {
                console.error("Failed to fetch campaigns:", err);
                setError(err.message || "Failed to access Royal Archives.");
                setLoading(false);
            })
            .finally(() => clearTimeout(timer));

        return () => clearTimeout(timer);
    }, [loading]);

    const handleResume = async (warId: string) => {
        navigate(`/war-room/${warId}`);
    };

    const [isResignModalOpen, setIsResignModalOpen] = useState(false);
    const [selectedWarId, setSelectedWarId] = useState<string | null>(null);

    const handleResignClick = (warId: string) => {
        setSelectedWarId(warId);
        setIsResignModalOpen(true);
    };

    const confirmResign = async () => {
        if (!selectedWarId) return;
        try {
            await SupabaseService.resignWar(selectedWarId);
            // Refresh
            const w = await SupabaseService.getAllWars();
            setWars(w);
            setIsResignModalOpen(false);
            setSelectedWarId(null);
        } catch (err: any) {
            setError(err.message);
            setIsResignModalOpen(false);
        }
    };

    const handleNewCampaign = () => {
        navigate('/drafting');
    };

    // Sort: Active first, then by date desc
    const activeWars = wars.filter(w => w.status === 'ACTIVE' || w.status === 'DRAFTING').sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    const pastWars = wars.filter(w => w.status === 'VICTORY' || w.status === 'DEFEAT').sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    if (loading) {
        return (
            <div className="min-h-screen bg-tudor-black flex flex-col items-center justify-center p-8">
                <div className="text-tudor-gold animate-pulse tracking-widest uppercase mb-6">Loading Royal Archives...</div>

                {showTimeout && (
                    <div className="bg-zinc-900/80 border border-tudor-red/30 p-6 rounded-lg text-center animate-fade-in max-w-sm">
                        <p className="text-zinc-400 text-xs uppercase tracking-wider mb-4">Connection to archives is delayed.</p>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => window.location.reload()}
                                className="px-4 py-2 border border-tudor-gold/30 text-tudor-gold text-xs uppercase hover:bg-tudor-gold/10 transition-colors"
                            >
                                Retry
                            </button>
                            <button
                                onClick={async () => { await signOut(); navigate('/'); }}
                                className="px-4 py-2 border border-red-900/50 text-red-500 text-xs uppercase hover:bg-red-900/20 transition-colors"
                            >
                                Force Sign Out
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-tudor-black flex flex-col items-center justify-center p-8">
                <div className="text-red-500 font-mono mb-4">ARCHIVE ERROR: {error}</div>
                <button
                    onClick={async () => { await signOut(); navigate('/'); }}
                    className="px-6 py-3 bg-zinc-900 text-zinc-400 hover:text-white uppercase tracking-widest text-xs border border-zinc-700 hover:border-zinc-500 transition-all"
                >
                    Return to Landing
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-tudor-black p-4 md:p-10 pb-20">
            <div className="max-w-5xl mx-auto space-y-12">

                {/* Header */}
                <header className="flex flex-col md:flex-row justify-between items-center border-b border-tudor-gold/30 pb-6 gap-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-tudor-gold/10 border border-tudor-gold rounded bg-opacity-20">
                            <Scroll className="text-tudor-gold w-8 h-8" />
                        </div>
                        <div>
                            <h1 className="text-3xl md:text-4xl font-serif text-tudor-gold tracking-widest uppercase">Royal Archives</h1>
                            <p className="text-gray-400 text-sm tracking-wider">Manage your campaigns across history</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/history')}
                            className="group flex items-center gap-3 bg-zinc-800 text-gray-300 border border-zinc-700 hover:border-tudor-gold/50 px-6 py-3 rounded font-bold uppercase tracking-widest hover:text-tudor-gold transition-all"
                        >
                            <History className="w-5 h-5" />
                            <span>Battle Log</span>
                        </button>

                        <button
                            onClick={handleNewCampaign}
                            className="group flex items-center gap-3 bg-tudor-gold text-tudor-black px-6 py-3 rounded font-bold uppercase tracking-widest hover:bg-yellow-600 transition-all shadow-[0_0_15px_rgba(197,160,89,0.3)] hover:shadow-[0_0_25px_rgba(197,160,89,0.5)]"
                        >
                            <PlusCircle className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                            <span>New Campaign</span>
                        </button>
                    </div>
                </header>

                {/* Active Campaigns */}
                <section>
                    <h2 className="text-xl text-white font-serif tracking-widest uppercase mb-6 flex items-center gap-2">
                        <Swords className="text-tudor-gold w-5 h-5" /> Active Fronts
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* VALOR BATTLE CARD (Quick Play)
                        <div
                            onClick={() => setIsQuickPlayModalOpen(true)}
                            className="bg-zinc-900/40 border border-zinc-700 hover:border-tudor-gold transition-all cursor-pointer p-6 rounded group relative overflow-hidden min-h-[160px] flex flex-col justify-between"
                        >
                            <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(212,175,55,0.05)_50%,transparent_75%)] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

                            <div className="flex justify-between items-start relative z-10">
                                <div>
                                    <div className="text-xs uppercase tracking-widest mb-1 text-emerald-500 flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                        Ranked Protocol
                                    </div>
                                    <div className="font-serif text-2xl text-white group-hover:text-tudor-gold transition-colors">Instant Skirmish</div>
                                </div>
                                <Swords className="text-zinc-600 group-hover:text-tudor-gold transition-colors" size={24} />
                            </div>

                            <p className="text-sm text-zinc-400 relative z-10 mt-2">
                                Rapid deployment. Single battle. Winner takes all.
                            </p>

                            <div className="mt-4 pt-4 border-t border-zinc-800 flex justify-between items-center relative z-10">
                                <span className="text-xs uppercase tracking-widest text-zinc-500 group-hover:text-white transition-colors">
                                    Deploy Now
                                </span>
                                <ChevronRight className="text-zinc-600 group-hover:text-tudor-gold group-hover:translate-x-1 transition-all" size={16} />
                            </div>
                        </div> */}



                        {activeWars.length === 0 ? (
                            <div className="col-span-1 p-8 border border-dashed border-gray-700 rounded flex flex-col items-center justify-center text-gray-500 gap-4 bg-white/5 min-h-[160px]">
                                <p>No active campaigns.</p>
                                <button onClick={handleNewCampaign} className="text-tudor-gold underline hover:text-white transition-colors">Start a new campaign</button>
                            </div>
                        ) : (
                            activeWars.map(war => {
                                const isHost = user && war.player_id === user.id;
                                return (
                                    <div key={war.id} className={`bg-tudor-dark border hover:border-tudor-gold transition-colors p-6 rounded group relative overflow-hidden min-h-[160px] ${!isHost ? 'border-red-900/40' : 'border-tudor-gray'}`}>
                                        {/* Top Right Cluster: Crown + Label */}
                                        <div className="absolute top-0 right-0 p-4 flex flex-col items-center gap-2 pointer-events-none">
                                            <div className="opacity-10 group-hover:opacity-20 transition-opacity">
                                                {isHost ? <Crown size={80} /> : <Swords size={80} className="text-red-900" />}
                                            </div>
                                            <div className="flex flex-col gap-2 items-end row">
                                                <div className="px-3 py-1 bg-green-900/50 border border-green-800 text-green-400 text-[10px] rounded uppercase tracking-wider font-bold shadow-lg backdrop-blur-sm">
                                                    Active
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex justify-between items-start mb-4 relative z-10">
                                            <div>
                                                <div className={`text-xs uppercase tracking-widest mb-1 ${!isHost ? 'text-red-400' : 'text-tudor-gold'}`}>
                                                    {!isHost ? "Invading Front" : "Sovereign Campaign"}
                                                </div>
                                                <div className="font-mono text-white text-lg">{war.id.substring(0, 8)}...</div>
                                            </div>
                                            {/* Label removed from here */}
                                        </div>

                                        <div className="grid grid-cols-3 gap-4 mb-6 relative z-10 max-w-[70%]">
                                            {war.is_ranked && (
                                                <div className="px-3 py-1 bg-tudor-gold/20 border border-tudor-gold text-tudor-gold text-[12px] rounded uppercase tracking-wider font-bold shadow-lg backdrop-blur-sm flex items-center gap-1 w-fit col-span-3 mb-1">
                                                    <Shield size={12} /> Ranked Protocol
                                                </div>
                                            )}
                                            {/* Row break if needed or just flex wrap */}

                                            <div>
                                                <div className="text-xs text-gray-500 uppercase">Role</div>
                                                <div className={`font-bold text-[10px] uppercase tracking-wider ${!isHost ? 'text-red-400' : 'text-tudor-gold'}`}>
                                                    {!isHost ? 'Aggressor' : 'Defender'}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-gray-500 uppercase">Battles</div>
                                                <div className="text-white font-mono">{war.battles_fought}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-gray-500 uppercase">Started</div>
                                                <div className="text-white font-mono">{new Date(war.created_at).toLocaleDateString()}</div>
                                            </div>
                                        </div>

                                        <div className="flex gap-4 mt-auto relative z-10 w-full">
                                            <button
                                                onClick={() => handleResignClick(war.id)}
                                                className="flex items-center justify-center p-3 bg-red-900/10 border border-red-900/40 hover:bg-red-900/30 hover:border-red-500 text-red-500 rounded transition-colors"
                                                title="Resign Campaign"
                                            >
                                                <Flag className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => navigate('/history', { state: { warId: war.id } })}
                                                className="flex items-center justify-center p-3 bg-zinc-800 border border-zinc-700 hover:border-tudor-gold/50 text-gray-400 hover:text-white rounded transition-colors"
                                                title="View Campaign History"
                                            >
                                                <History className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => handleResume(war.id)}
                                                className="flex-1 flex justify-between items-center bg-white/5 hover:bg-tudor-gold/20 hover:text-tudor-gold border border-tudor-gray/50 hover:border-tudor-gold group-hover:border-tudor-gold text-gray-300 py-3 px-4 rounded transition-all uppercase tracking-wider text-sm font-bold"
                                            >
                                                <span>Resume Command</span>
                                                <ChevronRight size={16} />
                                            </button>
                                        </div>
                                    </div>
                                )
                            })
                        )}
                    </div>
                </section>

                {/* Past Campaigns */}
                <section>
                    <h2 className="text-xl text-gray-400 font-serif tracking-widest uppercase mb-6 flex items-center gap-2 opacity-80">
                        <History className="w-5 h-5" /> Historical Records
                    </h2>

                    <div className="space-y-4">
                        {pastWars.length === 0 ? (
                            <p className="text-gray-600 italic">No history recorded yet.</p>
                        ) : (
                            pastWars.map(war => (
                                <div key={war.id} className="flex flex-col md:flex-row md:items-center justify-between p-4 border border-tudor-gray/30 bg-black/20 rounded hover:bg-white/5 transition-colors gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-2 h-12 rounded ${war.status === 'VICTORY' ? 'bg-tudor-gold' : 'bg-red-800'}`}></div>
                                        <div>
                                            <div className="font-mono text-gray-300">#{war.id}</div>
                                            <div className="text-xs text-gray-500">{new Date(war.created_at).toLocaleDateString()}</div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-8">
                                        <div className="text-center">
                                            <div className="text-xs text-gray-500 uppercase">Outcome</div>
                                            <div className={`font-bold tracking-wider ${war.status === 'VICTORY' ? 'text-tudor-gold' : 'text-red-500'}`}>{war.status}</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-xs text-gray-500 uppercase">Battles</div>
                                            <div className="text-gray-300 font-mono">{war.battles_fought}</div>
                                        </div>

                                        <button
                                            onClick={() => navigate('/history', { state: { warId: war.id } })}
                                            className="text-gray-500 hover:text-tudor-gold transition-colors"
                                            title="View History"
                                        >
                                            <History size={20} />
                                        </button>

                                        <button
                                            onClick={() => handleResume(war.id)} // View details essentially (Resume will effectively load it as read-only or show summary in WarRoom if implemented correctly)
                                            className="text-gray-400 hover:text-white transition-colors"
                                        >
                                            <ChevronRight />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </section>

                <QuickPlayModal isOpen={isQuickPlayModalOpen} onClose={() => setIsQuickPlayModalOpen(false)} />

                {/* Resign Modal */}
                {isResignModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-fade-in">
                        <div className="w-full max-w-md bg-zinc-900 border border-red-900/50 p-8 rounded-sm relative shadow-2xl text-center">
                            <h2 className="text-2xl font-serif text-red-500 mb-4 uppercase tracking-widest">Formal Resignation</h2>
                            <p className="text-zinc-400 mb-8 text-sm leading-relaxed">
                                Are you sure you wish to surrender this campaign? This action is irreversible and will record a <span className="text-red-400 font-bold">DEFEAT</span> on your permanent record.
                            </p>

                            <div className="flex gap-4">
                                <button
                                    onClick={() => setIsResignModalOpen(false)}
                                    className="flex-1 py-3 border border-zinc-700 text-zinc-500 uppercase tracking-widest text-xs hover:text-white"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmResign}
                                    className="flex-1 py-3 bg-red-900/80 text-white border border-red-700 uppercase tracking-widest text-xs font-bold hover:bg-red-800"
                                >
                                    Confirm Defeat
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Campaigns;
